import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Mail, Award, GraduationCap, BookOpen, ExternalLink } from 'lucide-react';
import { principalInvestigator } from '../data/team';

const PIPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-20">
        <section className="py-28 bg-gradient-to-br from-slate-50 to-white relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-100/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-100/30 rounded-full blur-3xl"></div>
          
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            {/* Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full glass-light border border-indigo-200 mb-8 shadow-lg shadow-indigo-100/50">
                <GraduationCap className="h-5 w-5 text-indigo-600" />
                <span className="text-sm font-bold text-indigo-700 tracking-wide">PRINCIPAL INVESTIGATOR</span>
              </div>
              <h1 className="text-5xl md:text-7xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.02em' }}>
                {principalInvestigator.name}
              </h1>
              <p className="text-2xl text-indigo-600 font-semibold mb-2">{principalInvestigator.credentials}</p>
              <p className="text-xl text-slate-600">{principalInvestigator.fullTitle}</p>
              <div className="w-40 h-2 bg-gradient-to-r from-indigo-600 via-cyan-500 to-amber-500 mx-auto rounded-full shadow-lg mt-6"></div>
            </div>

            {/* Photo and Bio */}
            <div className="glass-light rounded-3xl overflow-hidden shadow-2xl border-2 border-indigo-100 mb-12">
              <div className="grid md:grid-cols-5 gap-0">
                <div className="md:col-span-2 relative overflow-hidden bg-gradient-to-br from-indigo-100 to-cyan-100 flex items-center justify-center p-8">
                  <img 
                    src={principalInvestigator.photo} 
                    alt={principalInvestigator.name}
                    className="w-48 h-48 rounded-full object-cover shadow-2xl border-4 border-white"
                  />
                </div>
                
                <div className="md:col-span-3 p-10">
                  <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    <BookOpen className="h-7 w-7 text-indigo-600" />
                    About
                  </h2>
                  <div className="text-base md:text-lg text-slate-700 leading-relaxed space-y-4">
                    {principalInvestigator.bio.split('\n\n').map((paragraph, idx) => (
                      <p key={idx}>{paragraph}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Awards & Honors */}
            <div className="glass-light rounded-3xl p-10 shadow-xl border border-amber-100 mb-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                <Award className="h-7 w-7 text-amber-600" />
                Selected Awards & Honors
              </h2>
              <div className="space-y-3">
                {principalInvestigator.awards.map((award, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-xl hover-lift">
                    <div className="w-2 h-2 rounded-full bg-amber-500 mt-2 flex-shrink-0"></div>
                    <p className="text-lg text-slate-700">{award}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Affiliations */}
            <div className="glass-light rounded-3xl p-10 shadow-xl border border-cyan-100">
              <h2 className="text-3xl font-bold text-slate-900 mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Affiliations
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {principalInvestigator.affiliations.map((affiliation, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-xl">
                    <div className="w-2 h-2 rounded-full bg-cyan-500 mt-2 flex-shrink-0"></div>
                    <p className="text-slate-700">{affiliation}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* External Links */}
            <div className="mt-12 text-center">
              <p className="text-slate-600 mb-6 text-lg">Professional Profiles</p>
              <div className="flex flex-wrap justify-center gap-4">
                <a 
                  href="https://scholar.google.com/citations?hl=en&user=RQ_3xm4AAAAJ"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 premium-gradient text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-indigo-500/50 transition-all duration-300 flex items-center gap-2"
                >
                  Google Scholar
                  <ExternalLink className="h-4 w-4" />
                </a>
                <a 
                  href="https://orcid.org/0000-0002-9175-8110"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 glass-light border border-indigo-200 text-indigo-700 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center gap-2"
                >
                  ORCID
                  <ExternalLink className="h-4 w-4" />
                </a>
                <a 
                  href="https://www.researchgate.net/profile/Sonali-Chaturvedi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 glass-light border border-cyan-200 text-cyan-700 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center gap-2"
                >
                  ResearchGate
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PIPage;